import {IconName} from '@udemy/icons';

import {PopQuizLectureType, PopQuizRelatedLecture} from './types';

const LECTURE_TYPENAME_TO_TYPE: Record<string, PopQuizLectureType> = {
    VideoLecture: 'video',
    ArticleLecture: 'article',
    PresentationLecture: 'presentation',
    VideoMashupLecture: 'video_mashup',
    AudioLecture: 'audio',
    FileLecture: 'file',
    EBookLecture: 'file',
    ExternalLecture: 'external_link',
};

export function mapLectureTypename(typename: string): PopQuizLectureType {
    return LECTURE_TYPENAME_TO_TYPE[typename] ?? 'missing_content';
}

interface RawRelatedLecture {
    __typename: string;
    id: string;
    title: string;
    urlLanding: string;
}

// Shared by both the MultipleChoiceQuizItem and MultiSelectQuizItem branches of the
// supportedItems mapper — `relatedLectures` is undefined when queried with
// includeRelatedLectures: false (i.e. popQuizV2ExperienceEnabled is off).
export function mapRelatedLectures(
    lectures: RawRelatedLecture[] | undefined,
): PopQuizRelatedLecture[] {
    return (lectures ?? []).map((lecture) => ({
        id: lecture.id,
        title: lecture.title,
        url: lecture.urlLanding,
        type: mapLectureTypename(lecture.__typename),
    }));
}

// Icon glyph shown on the lecture link CTA, per lecture content type.
const LECTURE_TYPE_TO_ICON_GLYPH: Record<PopQuizLectureType, IconName> = {
    video: 'video',
    article: 'article',
    presentation: 'presentation',
    video_mashup: 'video-mashup',
    audio: 'audio',
    file: 'download-file',
    external_link: 'link',
    missing_content: 'blank',
};

export function mapLectureTypeToIconGlyph(type: PopQuizLectureType): IconName {
    return LECTURE_TYPE_TO_ICON_GLYPH[type];
}

/**
 * Converts a GraphQL `ID` (always a string) to the numeric id schema-store's event fields
 * expect. Returns `null` instead of `NaN` when the id isn't numeric, so callers can skip firing
 * an event with a bad id rather than publish `NaN`.
 */
export function toEventId(id: string): number | null {
    const numericId = Number(id);
    return Number.isFinite(numericId) ? numericId : null;
}
